import type { RootState } from 'src/redux/store';

import * as z from 'zod';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Box,
  Button,
  Dialog,
  Tooltip,
  TextField,
  IconButton,
  DialogTitle,
  DialogContent,
  InputAdornment,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

import { useResetPasswordMutation } from '../slice/authApi';

interface ChangePasswordFormProps {
  open: boolean;
  onCloseHandler: () => void;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ open, onCloseHandler }) => {
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);
  const { t } = useTranslate('translation');

  const [changePassword] = useResetPasswordMutation();

  // ✅ Schema validation using zod
  const ChangePasswordSchema = z
    .object({
      newPassword: z
        .string()
        .min(6, {
          message: t('PASSWORD_MIN_LENGTH', {
            name: t('NEW_PASSWORD')
          })
        })
        .regex(/[a-z]/, {
          message: t('REQUIRE_LOWERCASE', {
            name: t('NEW_PASSWORD')
          })
        })
        .regex(/[A-Z]/, {
          message: t('REQUIRE_UPPERCASE', {
            name: t('NEW_PASSWORD')
          })
        })
        .regex(/[^a-zA-Z0-9]/, {
          message: t('REQUIRE_SPECIAL_CHAR', {
            name: t('NEW_PASSWORD')
          })
        })
        .nonempty({ message: `${t('NEW_PASSWORD')} ${t('IS_REQUIRED')}` }),
      confirmPassword: z
        .string()
        .nonempty({ message: `${t('CONFIRM_PASSWORD')} ${t('IS_REQUIRED')}` }),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      path: ['confirmPassword'],
      message: t('PASSWORDS_DO_NOT_MATCH'),
    });



  type ChangePasswordSchemaType = z.infer<typeof ChangePasswordSchema>;

  const methods = useForm<ChangePasswordSchemaType>({
    mode: "all",
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const { handleSubmit, register, reset, formState: { errors } } = methods;

  const [showPassword, setShowPassword] = useState({
    new: false,
    confirm: false,
  });

  const toggleVisibility = (key: keyof typeof showPassword) => {
    setShowPassword((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const resetEyeIcons = () => {
    setShowPassword({ new: false, confirm: false });
  };

  const onSubmit = async (data: ChangePasswordSchemaType) => {
    try {
      const userId = loginApiData && 'id' in loginApiData ? loginApiData.id : '';
      await changePassword({
        user_id: userId,
        newPassword: data.newPassword,
      }).unwrap();

      toast.success(t('PASSWORD_CHANGED_SUCCESSFULLY'));
    } catch (error) {
      toast.error(t('PASSWORD_UPDATION_FAILED'));
    }

    onCloseHandler();
    resetEyeIcons();
    reset();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={() => {
        resetEyeIcons();
        onCloseHandler();
        reset();
      }}
      sx={{
        '& .MuiPaper-rounded': {
          p: 0.5,
          boxShadow: '0px 0px 30px rgba(255, 255, 255, 0.1)',
        },
      }}
    >
      <DialogTitle>{t('CHANGE_PASSWORD')}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* New Password */}
          <TextField
            fullWidth
            label={t('NEW_PASSWORD')}
            type={showPassword.new ? 'text' : 'password'}
            margin="normal"
            {...register('newPassword')}
            error={!!errors.newPassword}
            helperText={errors.newPassword?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPassword.new ? t('HIDE') : t('SHOW')}>
                    <IconButton onClick={() => toggleVisibility('new')}>
                      <Iconify
                        icon={showPassword.new ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        sx={{ color: 'var(--palette-action-active)' }}
                      />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          {/* Confirm Password */}
          <TextField
            fullWidth
            label={t('CONFIRM_PASSWORD')}
            type={showPassword.confirm ? 'text' : 'password'}
            margin="normal"
            {...register('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword?.message}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPassword.confirm ? t('HIDE') : t('SHOW')}>
                    <IconButton onClick={() => toggleVisibility('confirm')}>
                      <Iconify
                        icon={showPassword.confirm ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                        sx={{ color: 'var(--palette-action-active)' }}
                      />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'end', my: 2 }}>
            <Button type="submit" variant="contained" sx={{ ml: 2 }}>
              {t('UPDATE_PASSWORD')}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordForm;


// import type { RootState } from 'src/redux/store';

// import { useForm } from 'react-hook-form';
// import React, { useState, useEffect } from 'react';

// import {
//   Box,
//   Button,
//   Dialog,
//   Tooltip,
//   TextField,
//   IconButton,
//   DialogTitle,
//   DialogContent,
//   InputAdornment,
// } from '@mui/material';

// import { useTranslate } from 'src/locales';
// import { useAppSelector } from 'src/hook/hooks';

// import { toast } from 'src/components/snackbar';
// import { Iconify } from 'src/components/iconify';

// import { useResetPasswordMutation } from '../slice/authApi';

// interface FormData {
//   currentPassword: string;
//   newPassword: string;
//   confirmPassword: string;
// }

// interface ChangePasswordFormProps {
//   open: boolean;
//   onCloseHandler: () => void;
// }

// const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ open, onCloseHandler }) => {
//   const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);
//   const { t } = useTranslate('translation');

//   const [changePassword] = useResetPasswordMutation();
//   const {
//     register,
//     handleSubmit,
//     watch,
//     formState: { errors },
//     reset,
//   } = useForm<FormData>();

//   const [showPassword, setShowPassword] = useState({
//     current: false,
//     new: false,
//     confirm: false,
//   });

//   const toggleVisibility = (key: keyof typeof showPassword) => {
//     setShowPassword((prev) => ({ ...prev, [key]: !prev[key] }));
//   };

//   const resetEyeIcons = () => {
//     setShowPassword({
//       current: false,
//       new: false,
//       confirm: false,
//     });
//   };

//   const onSubmit = async (data: FormData) => {
//     try {
//       const userId = loginApiData && 'id' in loginApiData ? loginApiData.id : '';
//       await changePassword({
//         user_id: userId,
//         currentPassword: data.currentPassword,
//         newPassword: data.newPassword,
//       }).unwrap();

//       toast.success(t('PASSWORD_CHANGED_SUCCESSFULLY'));
//     } catch (error) {
//       toast.error(t('PASSWORD_UPDATION_FAILED'));

//       // optionally show error message to user here
//     }

//     onCloseHandler(); // close dialog after success
//     resetEyeIcons(); // reset visibility states
//     reset(); // reset only after success
//   };

//   return (
//     <Dialog
//       fullWidth
//       maxWidth="sm"
//       open={open}
//       onClose={() => {
//         resetEyeIcons();
//         onCloseHandler();
//         reset(); // reset form when dialog is closed
//       }}
//       sx={{
//         '& .MuiPaper-rounded': {
//           p: 0.5,
//           boxShadow: '0px 0px 30px rgba(255, 255, 255, 0.1)',
//         },
//       }}
//     >
//       <DialogTitle>{t('CHANGE_PASSWORD')}</DialogTitle>
//       <DialogContent>
//         <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
//           {/* Current Password */}
//           <TextField
//             fullWidth
//             label={t('CURRENT_PASSWORD')}
//             type={showPassword.current ? 'text' : 'password'}
//             margin="normal"
//             {...register('currentPassword', {
//               required: `${t('CURRENT_PASSWORD')} ${t('IS_REQUIRED')}`,
//             })}
//             error={!!errors.currentPassword}
//             helperText={errors.currentPassword?.message}
//             InputProps={{
//               endAdornment: (
//                 <InputAdornment position="end">
//                   <Tooltip title={showPassword.current ? t('HIDE') : t('SHOW')}>
//                     <IconButton onClick={() => toggleVisibility('current')}>
//                       <Iconify
//                         icon={showPassword.current ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
//                         sx={{ color: 'var(--palette-action-active)' }}
//                       />
//                     </IconButton>
//                   </Tooltip>
//                 </InputAdornment>
//               ),
//             }}
//           />

//           {/* New Password */}
//           <TextField
//             fullWidth
//             label={t('NEW_PASSWORD')}
//             type={showPassword.new ? 'text' : 'password'}
//             margin="normal"
//             {...register('newPassword', {
//               required: `${t('NEW_PASSWORD')} ${t('IS_REQUIRED')}`,
//               minLength: {
//                 value: 6,
//                 message: t('PASSWORD_MIN_LENGTH'),
//               },
//             })}
//             error={!!errors.newPassword}
//             helperText={errors.newPassword?.message}
//             InputProps={{
//               endAdornment: (
//                 <InputAdornment position="end">
//                   <Tooltip title={showPassword.new ? t('HIDE') : t('SHOW')}>
//                     <IconButton onClick={() => toggleVisibility('new')}>
//                       <Iconify
//                         icon={showPassword.new ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
//                         sx={{ color: 'var(--palette-action-active)' }}
//                       />
//                     </IconButton>
//                   </Tooltip>
//                 </InputAdornment>
//               ),
//             }}
//           />

//           {/* Confirm Password */}
//           <TextField
//             fullWidth
//             label={t('CONFIRM_PASSWORD')}
//             type={showPassword.confirm ? 'text' : 'password'}
//             margin="normal"
//             {...register('confirmPassword', {
//               required: `${t('CONFIRM_PASSWORD')} ${t('IS_REQUIRED')}`,
//               validate: (value) => value === watch('newPassword') || 'Passwords do not match',
//             })}
//             error={!!errors.confirmPassword}
//             helperText={errors.confirmPassword?.message}
//             InputProps={{
//               endAdornment: (
//                 <InputAdornment position="end">
//                   <Tooltip title={showPassword.confirm ? t('HIDE') : t('SHOW')}>
//                     <IconButton onClick={() => toggleVisibility('confirm')}>
//                       <Iconify
//                         icon={showPassword.confirm ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
//                         sx={{ color: 'var(--palette-action-active)' }}
//                       />
//                     </IconButton>
//                   </Tooltip>
//                 </InputAdornment>
//               ),
//             }}
//           />

//           <Box sx={{ display: 'flex', justifyContent: 'end', my: 2 }}>
//             <Button type="submit" variant="contained" sx={{ ml: 2 }}>
//               {t('UPDATE_PASSWORD')}
//             </Button>
//           </Box>
//         </Box>
//       </DialogContent>
//     </Dialog>
//   );
// };

// export default ChangePasswordForm;
